import React, { useState, useEffect } from "react";
import { db } from "../../firebase/config";
import { collection, query, onSnapshot, getDocs, where, orderBy, Timestamp } from "firebase/firestore";
import { useTheme } from "../../context/ThemeContext";
import Header from "../../components/Header";

export default function SuperAdminMetricas() {
  const { theme } = useTheme();
  const [periodo, setPeriodo] = useState('mes');
  const [loading, setLoading] = useState(true);
  const [clinicas, setClinicas] = useState([]);
  const [metricas, setMetricas] = useState({
    totalClinicas: 0, clinicasActivas: 0, clinicasTrial: 0, clinicasSuspendidas: 0,
    totalUsuarios: 0, nuevasClinicasPeriodo: 0,
    ingresosMesUSD: 0, ingresosMesARS: 0,
    clinicasPorPlan: { starter: 0, advanced: 0, pro: 0 },
    crecimientoMensual: [],
  });

  const btnStyle = (activo) => ({ padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', border: activo ? 'none' : `0.5px solid ${theme.borderInput}`, background: activo ? theme.accent : 'transparent', color: activo ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: activo ? '600' : '400' });

  function getRango() {
    const ahora = new Date();
    const inicio = new Date();
    if (periodo === 'mes') { inicio.setDate(1); inicio.setHours(0,0,0,0); }
    else if (periodo === 'trimestre') { inicio.setMonth(ahora.getMonth() - 3); inicio.setHours(0,0,0,0); }
    else if (periodo === 'anio') { inicio.setMonth(0); inicio.setDate(1); inicio.setHours(0,0,0,0); }
    return { inicio, fin: ahora };
  }

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'clinicas'), orderBy('creadoEn', 'desc')), async snap => {
      const clinicasData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setClinicas(clinicasData);

      const { inicio } = getRango();
      const nuevas = clinicasData.filter(c => { const f = c.creadoEn?.toDate ? c.creadoEn.toDate() : new Date(c.creadoEn); return f >= inicio; });

      const porPlan = { starter: 0, advanced: 0, pro: 0 };
      clinicasData.forEach(c => { if (c.plan && porPlan[c.plan] !== undefined) porPlan[c.plan]++; });

      const snapUsuarios = await getDocs(collection(db, 'usuarios'));
      const totalUsuarios = snapUsuarios.size;

      const snapPagos = await getDocs(query(collection(db, 'pagos'), where('fecha', '>=', Timestamp.fromDate(inicio))));
      const pagos = snapPagos.docs.map(d => d.data());
      const ingresosUSD = pagos.filter(p => p.moneda === 'USD' && !p.pagoRechazado).reduce((a, p) => a + (p.monto || 0), 0);
      const ingresosARS = pagos.filter(p => p.moneda === 'ARS' && !p.pagoRechazado).reduce((a, p) => a + (p.monto || 0), 0);

      const crecimiento = [];
      for (let i = 5; i >= 0; i--) {
        const mesInicio = new Date(); mesInicio.setMonth(mesInicio.getMonth() - i); mesInicio.setDate(1); mesInicio.setHours(0,0,0,0);
        const mesFin = new Date(mesInicio); mesFin.setMonth(mesFin.getMonth() + 1);
        const count = clinicasData.filter(c => { const f = c.creadoEn?.toDate ? c.creadoEn.toDate() : new Date(c.creadoEn || 0); return f >= mesInicio && f < mesFin; }).length;
        const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
        crecimiento.push({ mes: meses[mesInicio.getMonth()], count });
      }

      setMetricas({
        totalClinicas: clinicasData.length,
        clinicasActivas: clinicasData.filter(c => c.estado === 'activa').length,
        clinicasTrial: clinicasData.filter(c => c.estado === 'trial').length,
        clinicasSuspendidas: clinicasData.filter(c => c.estado === 'suspendida').length,
        totalUsuarios, nuevasClinicasPeriodo: nuevas.length,
        ingresosMesUSD: ingresosUSD, ingresosMesARS: ingresosARS,
        clinicasPorPlan: porPlan, crecimientoMensual: crecimiento,
      });
      setLoading(false);
    });
    return unsub;
  }, [periodo]);

  const maxCrecimiento = Math.max(...metricas.crecimientoMensual.map(m => m.count), 1);

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Métricas globales" backLabel="Super Admin" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div style={{ display: 'flex', gap: '6px' }}>
          {[['mes','Este mes'],['trimestre','Trimestre'],['anio','Este año']].map(([v, l]) => (
            <button key={v} onClick={() => setPeriodo(v)} style={btnStyle(periodo === v)}>{l}</button>
          ))}
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Calculando métricas...</div>
          : <>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>Clínicas</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                  {[
                    { label: 'Total registradas', valor: metricas.totalClinicas, color: theme.accent, bg: theme.infoBg },
                    { label: 'Activas', valor: metricas.clinicasActivas, color: theme.success, bg: theme.successBg },
                    { label: 'En trial', valor: metricas.clinicasTrial, color: theme.warning, bg: theme.warningBg },
                    { label: 'Suspendidas', valor: metricas.clinicasSuspendidas, color: theme.error, bg: theme.errorBg },
                    { label: 'Nuevas en período', valor: metricas.nuevasClinicasPeriodo, color: theme.accent, bg: theme.infoBg },
                    { label: 'Total usuarios', valor: metricas.totalUsuarios, color: theme.secondary, bg: theme.bgCard },
                  ].map(item => (
                    <div key={item.label} style={{ background: item.bg, padding: '12px 14px', borderRadius: '10px' }}>
                      <div style={{ fontSize: '11px', color: item.color, marginBottom: '4px', fontWeight: '500' }}>{item.label}</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: item.color }}>{item.valor}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>Distribución por plan</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { plan: 'starter', label: 'Starter', color: '#00B8FF' },
                    { plan: 'advanced', label: 'Advanced', color: '#00D084' },
                    { plan: 'pro', label: 'Pro', color: '#B478FF' },
                  ].map(item => {
                    const count = metricas.clinicasPorPlan[item.plan];
                    const pct = metricas.clinicasActivas > 0 ? ((count / metricas.clinicasActivas) * 100).toFixed(0) : 0;
                    return (
                      <div key={item.plan} style={{ background: theme.bgCard, borderRadius: '10px', padding: '14px', border: `0.5px solid ${item.color}40` }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: item.color, marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontSize: '28px', fontWeight: '700', color: theme.primary }}>{count}</div>
                        <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '8px' }}>{pct}% de activas</div>
                        <div style={{ height: '4px', background: theme.border, borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: item.color, borderRadius: '99px' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>Facturación del período</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                  {[
                    { label: 'Ingresos USD', valor: `USD ${metricas.ingresosMesUSD.toFixed(2)}`, color: theme.accent, bg: theme.infoBg },
                    { label: 'Ingresos ARS', valor: `$${metricas.ingresosMesARS.toLocaleString('es-AR')}`, color: theme.success, bg: theme.successBg },
                    { label: 'MRR estimado USD', valor: `USD ${(metricas.clinicasActivas * 29 + metricas.clinicasTrial * 0).toFixed(0)}`, color: theme.secondary, bg: theme.bgCard, sub: 'Basado en plan Starter promedio' },
                  ].map(item => (
                    <div key={item.label} style={{ background: item.bg, padding: '14px 16px', borderRadius: '10px' }}>
                      <div style={{ fontSize: '11px', color: item.color, marginBottom: '4px', fontWeight: '500' }}>{item.label}</div>
                      <div style={{ fontSize: '22px', fontWeight: '700', color: item.color }}>{item.valor}</div>
                      {item.sub && <div style={{ fontSize: '10px', color: theme.secondary, marginTop: '2px' }}>{item.sub}</div>}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>Crecimiento — últimos 6 meses</div>
                <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '120px' }}>
                    {metricas.crecimientoMensual.map((m, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: theme.accent }}>{m.count}</div>
                        <div style={{ width: '100%', background: theme.accent, borderRadius: '4px 4px 0 0', height: `${Math.max((m.count / maxCrecimiento) * 80, m.count > 0 ? 8 : 2)}px`, transition: 'height 0.3s', opacity: i === metricas.crecimientoMensual.length - 1 ? 1 : 0.6 }} />
                        <div style={{ fontSize: '10px', color: theme.secondary }}>{m.mes}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>Clínicas recientes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {clinicas.slice(0, 10).map(c => {
                    const fecha = c.creadoEn?.toDate ? c.creadoEn.toDate().toLocaleDateString('es-AR') : '—';
                    const estadoColor = { activa: theme.success, trial: theme.accent, suspendida: theme.error, pendiente_activacion: theme.warning, cancelada: theme.secondary };
                    return (
                      <div key={c.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{c.nombre}</div>
                          <div style={{ fontSize: '11px', color: theme.secondary }}>{c.email} · {fecha}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {c.plan && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '99px', background: theme.infoBg, color: theme.accent, fontWeight: '500' }}>{c.plan}</span>}
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: `${estadoColor[c.estado] || theme.secondary}20`, color: estadoColor[c.estado] || theme.secondary, fontWeight: '500' }}>
                            {c.estado?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
        }
      </div>
    </div>
  );
}